import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

const slides = [
  {
    before: 'transformation-client2-before.webp',
    after: 'transformation-client2-after.webp',
  },
  {
    before: 'transformation-1-before.webp',
    after: 'transformation-1-after.webp',
  },
  {
    before: 'transformation-client3-before.webp',
    after: 'transformation-client3-after.webp',
    beforePosition: 'center 100%',
  },
  {
    before: 'transformation-client4-before.webp',
    after: 'transformation-client4-after.webp',
  },
];

const Transformations: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [position, setPosition] = useState(50);
  const [failedSlides, setFailedSlides] = useState<Set<number>>(new Set());
  const draggingRef = useRef(false);

  const markSlideFailed = useCallback((index: number) => {
    setFailedSlides((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  }, []);

  // If the current slide's images fail to load (asset not added yet), skip to the next working one.
  useEffect(() => {
    if (!failedSlides.has(slideIndex) || failedSlides.size >= slides.length) return;
    let next = (slideIndex + 1) % slides.length;
    let guard = 0;
    while (failedSlides.has(next) && guard < slides.length) {
      next = (next + 1) % slides.length;
      guard++;
    }
    if (next !== slideIndex) {
      setSlideIndex(next);
      setPosition(50);
    }
  }, [failedSlides, slideIndex]);

  const updatePosition = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const percent = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, percent)));
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updatePosition(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    updatePosition(e.clientX);
  };

  const handlePointerUp = () => {
    draggingRef.current = false;
  };

  const goToSlide = (index: number) => {
    setSlideIndex(((index % slides.length) + slides.length) % slides.length);
    setPosition(50);
  };

  const slide = slides[slideIndex];

  return (
    <section className="bg-dark py-16 md:py-24">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <h2
            className="text-2xl md:text-4xl font-extrabold uppercase tracking-wide text-white mb-3"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Real clients. Real transformations.
          </h2>
          <p className="text-primary/80 text-base md:text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
            No filters. Drag the line to see for yourself.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="max-w-md mx-auto select-none"
        >
          <p
            className="text-center text-secondary text-sm md:text-base font-bold uppercase tracking-[0.2em] mb-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Transform
          </p>

          <div className="relative flex items-center justify-center gap-3">
            {/* Prev arrow */}
            <button
              onClick={() => goToSlide(slideIndex - 1)}
              aria-label="Previous client"
              className="shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div
              ref={containerRef}
              className="relative w-full max-w-sm rounded-2xl overflow-hidden cursor-ew-resize touch-none shadow-2xl"
              style={{ aspectRatio: '458/714' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            >
              {/* After image (full) */}
              <img
                src={`${process.env.PUBLIC_URL}/images/${slide.after}`}
                alt="After"
                loading={slideIndex === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                draggable={false}
                onError={() => markSlideFailed(slideIndex)}
              />
              {/* Before image (clipped) */}
              <div
                className="absolute inset-0 overflow-hidden pointer-events-none"
                style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/${slide.before}`}
                  alt="Before"
                  loading={slideIndex === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: slide.beforePosition || 'center' }}
                  draggable={false}
                  onError={() => markSlideFailed(slideIndex)}
                />
              </div>

              {/* Labels */}
              <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-wider text-white bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full pointer-events-none">
                Before
              </span>
              <span className="absolute top-4 right-4 text-xs font-bold uppercase tracking-wider text-white bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full pointer-events-none">
                After
              </span>

              {/* Divider line + handle */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white pointer-events-none"
                style={{ left: `calc(${position}% - 2px)` }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l-4 4 4 4m8-12l4 4-4 4" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Next arrow */}
            <button
              onClick={() => goToSlide(slideIndex + 1)}
              aria-label="Next client"
              className="shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Slide dots */}
          <div className="flex items-center justify-center gap-2 mt-5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                aria-label={`Go to client ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-200 ${
                  i === slideIndex ? 'w-6 bg-secondary' : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Transformations;
